import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ── Query key factories ──

// Filter + sort state that parameterizes a paginated list query key. Mirrors
// `DataGridQuery` from `@nymbl/nos2-rules`; kept structural to avoid a cycle.
interface ListKeyFilters {
  search?: string;
  status?: string;
  sort?: string;
  sortDir?: string;
}

// --- S3_FEATURE_START ---
export const fileKeys = {
  all: ["files"] as const,
  list: (filters: ListKeyFilters) => ["files", "list", filters] as const,
  detail: (id: string) => ["files", "detail", id] as const,
} as const;
// --- S3_FEATURE_END ---

export const userKeys = {
  all: ["users"] as const,
  list: (filters?: { search?: string }) => ["users", "list", filters] as const,
} as const;

export const personLinkKeys = {
  all: ["person-links"] as const,
  forUser: (userId: string) => ["person-links", "user", userId] as const,
  search: (email: string) => ["person-links", "search", email] as const,
} as const;

export const authKeys = {
  options: ["auth", "options"] as const,
} as const;

export const personKeys = {
  me: ["person", "me"] as const,
  profile: ["person", "profile"] as const,
} as const;

export const appKeys = {
  all: ["apps"] as const,
  mine: ["apps", "mine"] as const,
} as const;

export const devKeys = {
  seedAccounts: ["dev", "seed-accounts"] as const,
} as const;

export const emailKeys = {
  all: ["email"] as const,
  outbox: () => ["email", "outbox"] as const,
  templates: () => ["email", "templates"] as const,
  template: (key: string) => ["email", "templates", key] as const,
} as const;

export const auditKeys = {
  all: ["audit"] as const,
  list: (filters: ListKeyFilters) => ["audit", "list", filters] as const,
} as const;

export const timeKeys = {
  all: ["time"] as const,
  weeks: () => ["time", "week"] as const,
  week: (start: string) => ["time", "week", start] as const,
  assignableAll: () => ["time", "assignable"] as const,
  assignable: (q?: string, limit?: number) =>
    ["time", "assignable", q ?? "", limit ?? null] as const,
  /** The Favorites window's project list. */
  assignableProjects: () => ["time", "assignable", "projects"] as const,
  /** One project's tasks, when the Favorites window opens it. */
  assignableProject: (projectId: string) => ["time", "assignable", "project", projectId] as const,
  /** The person's assignments to one task, when a task link opens Log time. */
  assignableTask: (projectTaskId: string) => ["time", "assignable", "task", projectTaskId] as const,
  signOffs: () => ["time", "signoffs"] as const,
  monthEntriesAll: () => ["time", "month-entries"] as const,
  monthEntries: (month: string) => ["time", "month-entries", month] as const,
} as const;

export const teamKeys = {
  all: ["team"] as const,
  month: (month: string) => ["team", "month", month] as const,
  /** Every person-panel read (months, guidance, entries) for every report. */
  people: () => ["team", "person"] as const,
  personMonths: (personId: string, month: string) =>
    ["team", "person", personId, "months", month] as const,
  personGuidance: (personId: string, month: string) =>
    ["team", "person", personId, "guidance", month] as const,
  personEntries: (personId: string, month: string) =>
    ["team", "person", personId, "entries", month] as const,
  timeOff: (month: string) => ["team", "time-off", month] as const,
  signOffs: () => ["team", "signoffs"] as const,
  ptoRequests: () => ["team", "pto-requests"] as const,
  pendingApprovals: () => ["team", "pending-approvals"] as const,
} as const;

export const monthKeys = {
  all: ["month"] as const,
  summary: (month: string, span: number) => ["month", "summary", month, span] as const,
  demands: (month: string) => ["month", "demands", month] as const,
  tasks: (month: string) => ["month", "tasks", month] as const,
  kpis: (year: number) => ["month", "kpis", year] as const,
} as const;

export const incentiveKeys = {
  all: ["incentives"] as const,
  mine: () => ["incentives", "mine"] as const,
} as const;

export const ptoKeys = {
  all: ["pto"] as const,
  balances: (year: number) => ["pto", "balances", year] as const,
  requests: (year: number) => ["pto", "requests", year] as const,
  quote: (key: string) => ["pto", "quote", key] as const,
  approvals: (scope: string) => ["pto", "approvals", scope] as const,
  hrPending: () => ["pto", "hr-pending"] as const,
} as const;

export const expenseKeys = {
  all: ["expenses"] as const,
  year: (year: number) => ["expenses", "year", year] as const,
  options: () => ["expenses", "options"] as const,
  claim: (id: string) => ["expenses", "claim", id] as const,
  approvals: () => ["expenses", "approvals"] as const,
  pending: () => ["expenses", "pending"] as const,
} as const;

/** Mutation keys: a request dialog stays open while its write is in flight. */
export const ptoWriteKeys = {
  request: () => ["pto-write", "request"] as const,
} as const;

/**
 * Settling a new request whose send timed out, by its browser-made id. Outside `ptoKeys.all` on
 * purpose: a PTO write must not re-run the check (it would report the request saved twice).
 */
export const ptoCreateCheckKeys = {
  check: (id: string) => ["pto-create-check", id] as const,
} as const;

/** The Outlook calendar: connection, a week's meeting suggestions, and the person's match rules. */
export const calendarKeys = {
  all: ["calendar"] as const,
  status: ["calendar", "status"] as const,
  week: (start: string) => ["calendar", "week", start] as const,
  rules: ["calendar", "rules"] as const,
} as const;

export const jobKeys = {
  all: ["jobs"] as const,
  list: ["jobs", "list"] as const,
  runs: (filters: ListKeyFilters & { filters?: Record<string, string> }) =>
    ["jobs", "runs", filters] as const,
} as const;
